// src/odoo/customers/components/CustomerPaymentAging.tsx
// All comments are in English.

import React, { useMemo, useState } from "react";
import type { PaymentSummaryResponse } from "../api/overviewApi";
import type { InvoiceDocument } from "../api/documentsApi";
import { formatHKD } from "./utils";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
} from "recharts";
import { AlertCircle } from "lucide-react";

import CustomerOutstandingInvoicesDrawer from "./CustomerOutstandingInvoicesDrawer";

interface Props {
  data?: PaymentSummaryResponse;
  isLoading: boolean;
  error?: unknown;
  invoices?: InvoiceDocument[];
  isInvoicesLoading?: boolean;
}

interface AgingChartPoint {
  bucket: string;
  amount: number;
}

const AXIS_COLOR = "#64748b";
const SAFE_COLOR = "#22c55e"; // green
const MID_RISK_COLOR = "#fb923c"; // orange
const HIGH_RISK_COLOR = "#dc2626"; // red

/**
 * Map an aging bucket label to a risk color.
 * The labels come from the backend: "0-30", "31-60", "61-90", "90+".
 */
const getBucketColor = (label: string): string => {
  switch (label) {
    case "0-30":
      return SAFE_COLOR; // low risk
    case "31-60":
      return "#fde047"; // yellow
    case "61-90":
      return MID_RISK_COLOR; // medium risk
    case "90+":
      return HIGH_RISK_COLOR; // high risk
    default:
      return "#94a3b8"; // gray fallback
  }
};

export const CustomerPaymentAging: React.FC<Props> = ({
  data,
  isLoading,
  error,
  invoices,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Outstanding invoice count should be based on residual > 0,
  // not only on payment_state.
  const outstandingCount = useMemo(() => {
    if (!invoices || !invoices.length) return 0;

    return invoices.filter((inv) => {
      const residual =
        typeof inv.residual === "number" ? inv.residual : Number(inv.residual ?? 0);
      return residual > 0;
    }).length;
  }, [invoices]);

  if (isLoading) {
    return (
      <div className="p-4 border rounded-lg bg-card h-80 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">
          Loading payment &amp; aging data.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border rounded-lg bg-card h-80 flex items-center">
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div>
            <p className="font-medium">
              Failed to load payment &amp; aging data.
            </p>
            <p className="text-xs text-muted-foreground">
              Please try again later or adjust the date range.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 border rounded-lg bg-card h-80 flex items-center">
        <p className="text-sm text-muted-foreground">
          No payment &amp; aging data available for the current filters.
        </p>
      </div>
    );
  }

  const agingBuckets = data.aging_buckets ?? [];

  const chartData: AgingChartPoint[] = agingBuckets.map((bucket) => ({
    bucket: bucket.label,
    amount: bucket.amount,
  }));

  // Sort buckets in logical order: 0-30, 31-60, 61-90, 90+
  const bucketOrder = ["0-30", "31-60", "61-90", "90+"];
  chartData.sort(
    (a, b) => bucketOrder.indexOf(a.bucket) - bucketOrder.indexOf(b.bucket)
  );

  if (!chartData.length) {
    return (
      <div className="p-4 border rounded-lg bg-card h-80 flex items-center">
        <p className="text-sm text-muted-foreground">
          No aging data available.
        </p>
      </div>
    );
  }

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  return (
    <>
      <div className="p-4 border rounded-lg bg-card h-80 flex flex-col">
        {/* Title + Avg DSO + Outstanding button */}
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold">Payment &amp; Aging</h2>
          <div className="flex items-center gap-2">
            <div className="text-xs text-muted-foreground">
              Avg DSO:{" "}
              <span className="font-semibold">
                {data.average_dso != null
                  ? `${data.average_dso.toFixed(1)} days`
                  : "N/A"}
              </span>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-amber-500/70 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100 transition-colors"
              onClick={handleOpenDrawer}
              disabled={!outstandingCount}
            >
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-600 text-[10px] font-bold text-white">
                {outstandingCount}
              </span>
              <span>View outstanding invoices</span>
            </button>
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-[11px] text-muted-foreground mb-3">
          Buckets are based on invoice due dates and outstanding balances. Only
          invoices with remaining residual are considered in the aging chart.
        </p>

        {/* Chart */}
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 12, left: -10, bottom: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(148,163,184,0.5)"
              />
              <XAxis
                dataKey="bucket"
                stroke={AXIS_COLOR}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                stroke={AXIS_COLOR}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
                tickFormatter={(val: number) => `${val / 1000}k`}
              />
              <Tooltip
                formatter={(value: number) => formatHKD(value)}
                labelFormatter={(label: string) => `Bucket: ${label} days`}
              />
              <Bar dataKey="amount" radius={4}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={String(index)}
                    fill={getBucketColor(entry.bucket)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Drawer for outstanding invoices grouped by aging buckets */}
      <CustomerOutstandingInvoicesDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        invoices={invoices}
      />
    </>
  );
};

export default CustomerPaymentAging;
